import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { LicenseIcon } from '@uplift/icons';
import { ProviderMedicalRole } from '@uplift/types';
import { Alert, AlertProps, SupportPaymentLabel } from '@uplift/ui-v2';

import { useSelectedProviderContext } from '../selected-provider.context';
import { useSelectedProviderStyles } from '../styles';
import { CollapsableBulletList } from './CollapsableBulletList.component';

export type AcceptedInsurancesProps = {
  isPublicProfile?: boolean;
  forceMobile?: boolean;
};

// short_name list of most common insurances
const mostCommonInsurances = [
  'Aetna',
  'Anthem',
  'CareFirst',
  'Cigna',
  'FloridaBlue',
  'Medicare',
  'UnitedHealthcare',
];

export const AcceptedInsurances: React.VFC<AcceptedInsurancesProps> = props => {
  const { classes } = useSelectedProviderStyles();
  const { selectedProvider, matchedInsuranceCompany } = useSelectedProviderContext();
  const name = selectedProvider.nameDisplay.first;
  const isPsychiatrist = selectedProvider.medicalRole === ProviderMedicalRole.psychiatrist;

  if (props.isPublicProfile && selectedProvider?.supportedInsuranceCompanies?.length) {
    const commonInsurances = selectedProvider?.supportedInsuranceCompanies?.filter(insurance => mostCommonInsurances.includes(insurance.shortName)).map(i => i.shortName).sort() ?? [];
    const otherInsurances = selectedProvider?.supportedInsuranceCompanies?.filter(insurance => !mostCommonInsurances.includes(insurance.shortName)).map(i => i.shortName).sort() ?? [];

    return (
      <Box pb={props.forceMobile ? 3 : { xs: 3, sm: 2 }}>
        <CustomAlert severity="success" title={`${name} Accepts Insurance`} icon={<LicenseIcon />} forceMobile={props.forceMobile}>
          <Box mt={0.5}>
            <CollapsableBulletList
              listItems={[ ...commonInsurances, ...otherInsurances ]}
              previewMaxItems={commonInsurances.length || 3}
              seeMoreVariant="link"
            />
          </Box>
        </CustomAlert>
        <Box display="flex" flexDirection="column" alignItems="center" gap="0.25rem" textAlign="center">
          <Typography>Don’t see your insurance? {name} also accepts a</Typography>
          <SupportPaymentLabel
            isAcceptInsurance={false}
            isPsychiatrist={isPsychiatrist}
            className={classes.paymentLabel}
          />
        </Box>
      </Box>
    );
  }

  const price = isPsychiatrist ? '$175-$250' : '$125-$145';
  const insuranceName = matchedInsuranceCompany?.shortName ?? `Reduced Cash Price ${price} a session`;

  return (
    <CustomAlert severity="success" forceMobile={props.forceMobile}>
      <Typography>{name} accepts <Typography component="b" fontWeight={500}>{insuranceName}</Typography></Typography>
    </CustomAlert>
  );
};

const useCustomAlertStyles = makeStyles<{ forceMobile?: boolean }>({ name: 'CustomAlert' })((theme, { forceMobile }) => ({
  root: ({
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),

    ...(forceMobile && {
      [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(3, 0),
      }
    })
  }),
  message: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  icon: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  title: {
    marginBottom: 0,
    marginTop: 0,
  },
}));


const CustomAlert: React.FC<AlertProps & { forceMobile?: boolean }> = props => {
  const { classes } = useCustomAlertStyles(props, { props });

  return <Alert {...props} classes={classes} />;
};
